import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Col,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faPlus,
  faChevronDown,
  faChevronUp,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { actions, services } from '../../../context';
import { ConfigurationReadResponseItem, ConfigurationUniversalSource } from '../../types';

const SENSITIVE_KEYS = new Set(['pms_password', 'authorization']);

const NULL_KEY_PLACEHOLDER = '*NULL KEY*';
const keyOf = (item: ConfigurationReadResponseItem): string => item.key ?? NULL_KEY_PLACEHOLDER;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  instanceId: string;
}

interface ScopeBadge {
  label: string;
  color: string;
  name?: string;
  id?: string;
}

// Lookup maps resolving the GUID stored on a source to its human-readable name.
interface NameMaps {
  pims: Map<string, string>;
  bitwerx: Map<string, string>;
  partnerOffering: Map<string, string>;
}

// The scope levels a single source targets (a source can hit several at once, e.g. instance + pims).
const sourceLevels = (s: ConfigurationUniversalSource): string[] => {
  const levels: string[] = [];
  if (s.instanceScheduleId) levels.push('Schedule');
  if (s.instanceId) levels.push('Instance');
  if (s.partnerOfferingId) levels.push('PartnerOffering');
  if (s.bitwerxOfferingId) levels.push('BitwerxOffering');
  if (s.pimsVersionId) levels.push('PimsVersion');
  if (s.pimsId) levels.push('Pims');
  if (levels.length === 0) levels.push('Global');
  return levels;
};

const LEVEL_OPTIONS = [
  'Instance',
  'Schedule',
  'PartnerOffering',
  'BitwerxOffering',
  'PimsVersion',
  'Pims',
  'Global',
  'Unused',
] as const;

// A single source can target multiple scope dimensions at once (e.g. instance + pims),
// so return every applicable badge rather than just the highest one. Name-bearing levels
// (Pims, BitwerxOffering, PartnerOffering) carry the resolved name plus the raw id.
const scopeBadges = (s: ConfigurationUniversalSource, maps: NameMaps): ScopeBadge[] => {
  const badges: ScopeBadge[] = [];
  if (s.instanceScheduleId) badges.push({ label: 'Schedule', color: 'success' });
  if (s.instanceId) badges.push({ label: 'Instance', color: 'success' });
  if (s.partnerOfferingId)
    badges.push({
      label: 'PartnerOffering',
      color: 'warning',
      name: maps.partnerOffering.get(s.partnerOfferingId),
      id: s.partnerOfferingId,
    });
  if (s.bitwerxOfferingId)
    badges.push({
      label: 'BitwerxOffering',
      color: 'warning',
      name: maps.bitwerx.get(s.bitwerxOfferingId),
      id: s.bitwerxOfferingId,
    });
  if (s.pimsVersionId) badges.push({ label: 'PimsVersion', color: 'info' });
  if (s.pimsId)
    badges.push({ label: 'Pims', color: 'info', name: maps.pims.get(s.pimsId), id: s.pimsId });
  if (badges.length === 0) badges.push({ label: 'Global', color: 'secondary' });
  return badges;
};

// Renders the scope badges for a source: name prominent inside the badge, raw id de-emphasized beside it.
const ScopeBadges: React.FC<{ source: ConfigurationUniversalSource; maps: NameMaps }> = ({
  source,
  maps,
}) => (
  <>
    {scopeBadges(source, maps).map(b => (
      <Badge
        key={b.label}
        color={b.color}
        className="mr-1"
        title={b.id}
        style={b.id ? { cursor: 'help' } : undefined}>
        {b.name ? `${b.label}: ${b.name}` : b.label}
      </Badge>
    ))}
  </>
);

export const PracticeUniversalConfigModal: React.FC<Props> = ({ isOpen, onClose, instanceId }) => {
  const dispatch = useDispatch();
  const { configs, loading } = useSelector(
    (s: {
      configurationUniversal: { configs: ConfigurationReadResponseItem[]; loading: boolean };
    }) => s.configurationUniversal,
  );

  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Collapsible search / level filter (collapsed by default).
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [levelFilter, setLevelFilter] = useState(''); // '' = all levels

  // PIMS names come free from the global store (loaded app-wide on auth).
  const allPims = useSelector(
    (s: { pims: { allPims: { id: string; name: string }[] } }) => s.pims.allPims,
  );
  const pimsNames = useMemo(
    () => new Map((allPims ?? []).map(p => [p.id, p.name] as const)),
    [allPims],
  );

  // Offering names aren't reliably in any global store reachable from this (legacy) tree,
  // so fetch both lists once when the modal opens and build id→name maps.
  const [bitwerxNames, setBitwerxNames] = useState<Map<string, string>>(new Map());
  const [partnerOfferingNames, setPartnerOfferingNames] = useState<Map<string, string>>(new Map());

  const nameMaps = useMemo<NameMaps>(
    () => ({ pims: pimsNames, bitwerx: bitwerxNames, partnerOffering: partnerOfferingNames }),
    [pimsNames, bitwerxNames, partnerOfferingNames],
  );

  // Sort by the winning (selected) source's score descending, then by key A–Z.
  // Unused keys (no selected source) get score 0 and sink to the bottom.
  const sortedConfigs = useMemo(() => {
    return [...configs]
      .map(item => {
        const selected = item.sources.find(s => s.sourceId === item.selectedSourceId);
        return { item, score: selected?.score ?? 0 };
      })
      .sort((a, b) => b.score - a.score || keyOf(a.item).localeCompare(keyOf(b.item)))
      .map(x => x.item);
  }, [configs]);

  // Filter the (already sorted) list by key text and level. Keeps score/name ordering.
  const visibleConfigs = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return sortedConfigs.filter(item => {
      if (q && !keyOf(item).toLowerCase().includes(q)) return false;
      if (levelFilter) {
        const selected = item.sources.find(s => s.sourceId === item.selectedSourceId);
        if (levelFilter === 'Unused') return !selected;
        if (!selected) return false;
        return sourceLevels(selected).includes(levelFilter);
      }
      return true;
    });
  }, [sortedConfigs, searchText, levelFilter]);

  useEffect(() => {
    if (!isOpen) {
      setDraftValues({});
      setNewKey('');
      setNewValue('');
      setExpandedKeys(new Set());
      setSearchOpen(false);
      setSearchText('');
      setLevelFilter('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && instanceId) {
      void dispatch(actions.configurationUniversal.fetchUniversalConfigs(instanceId));
    }
  }, [isOpen, instanceId, dispatch]);

  // Fetch offering name lists on open (best-effort: on failure we fall back to showing IDs only).
  useEffect(() => {
    if (!isOpen) return;
    const axios = services.axios.Axios();
    let cancelled = false;
    void (async () => {
      try {
        const [bitwerxRes, partnerRes] = await Promise.all([
          axios.get('offerings/bitwerx'),
          axios.get('offerings/partners'),
        ]);
        if (cancelled) return;
        const bitwerx =
          (bitwerxRes as { data: { items: { id: string; name: string }[] } }).data.items ?? [];
        const partner =
          (partnerRes as { data: { data: { id: string; name: string }[] } }).data.data ?? [];
        setBitwerxNames(new Map(bitwerx.map(o => [o.id, o.name] as const)));
        setPartnerOfferingNames(new Map(partner.map(o => [o.id, o.name] as const)));
      } catch {
        // names are best-effort; leave maps empty and render IDs only
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleDraftChange = (key: string, value: string) => {
    setDraftValues(prev => ({ ...prev, [key]: value }));
  };

  const isDirty = (item: ConfigurationReadResponseItem) =>
    item.key in draftValues && draftValues[item.key] !== (item.value ?? '');

  const handleSave = async (item: ConfigurationReadResponseItem) => {
    setSavingKey(item.key);
    try {
      await dispatch(
        actions.configurationUniversal.addOrUpdateUniversalConfig(
          { key: item.key, value: draftValues[item.key], instanceId },
          instanceId,
        ),
      );
      setDraftValues(prev => {
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleRemove = async (item: ConfigurationReadResponseItem) => {
    setRemovingKey(item.key);
    try {
      await dispatch(
        actions.configurationUniversal.removeUniversalConfig(
          { key: item.key, instanceId },
          instanceId,
        ),
      );
    } finally {
      setRemovingKey(null);
    }
  };

  const handleAdd = async () => {
    if (!newKey.trim() || !newValue.trim()) return;
    setSaving(true);
    try {
      await dispatch(
        actions.configurationUniversal.addOrUpdateUniversalConfig(
          { key: newKey.trim(), value: newValue.trim(), instanceId },
          instanceId,
        ),
      );
      setNewKey('');
      setNewValue('');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <Modal style={{ minWidth: '75vw' }} isOpen={isOpen} toggle={onClose} centered={true}>
      <ModalHeader className="justify-content-between text-light">
        Advanced Practice Configurations
      </ModalHeader>
      <div className="text-light">
        Edited and new configurations only affect at instance level. Global, PIMS and Offerings
        settings will remain the same.
      </div>
      <ModalBody className="justify-content-center">
        <div className="px-3 pt-2">
          <Button color="secondary" size="sm" onClick={() => setSearchOpen(o => !o)}>
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            Search &amp; filter
            <FontAwesomeIcon icon={searchOpen ? faChevronUp : faChevronDown} className="ml-2" />
          </Button>

          <Collapse isOpen={searchOpen}>
            <Row className="pt-2 align-items-center">
              <Col md="7" xs="12" className="mb-2 mb-md-0">
                <input
                  className="w-100 p-2 rounded border"
                  placeholder="Search by key name…"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </Col>
              <Col md="4" xs="9">
                <select
                  className="w-100 p-2 rounded border"
                  value={levelFilter}
                  onChange={e => setLevelFilter(e.target.value)}>
                  <option value="">All levels</option>
                  {LEVEL_OPTIONS.map(l => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </Col>
              <Col md="1" xs="3" className="text-right">
                {(searchText || levelFilter) && (
                  <Button
                    color="link"
                    size="sm"
                    className="text-light p-0"
                    onClick={() => {
                      setSearchText('');
                      setLevelFilter('');
                    }}>
                    Clear
                  </Button>
                )}
              </Col>
            </Row>
          </Collapse>
        </div>

        {loading && <h3 className="text-center text-light">Loading...</h3>}

        {!loading && configs.length === 0 && (
          <p className="text-center text-light">No configurations found for this instance.</p>
        )}

        {!loading && configs.length > 0 && visibleConfigs.length === 0 && (
          <p className="text-center text-light">No configurations match your search.</p>
        )}

        {!loading &&
          visibleConfigs.map(item => {
            const selectedSource = item.sources.find(s => s.sourceId === item.selectedSourceId);
            const canDelete =
              selectedSource?.sourceEntity === 'ConfigurationUniversal' &&
              selectedSource?.instanceId != null;
            const isSensitive = SENSITIVE_KEYS.has(item.key);
            const isExpanded = expandedKeys.has(item.key);

            return (
              <React.Fragment key={item.key ?? item.sources[0]?.sourceId ?? NULL_KEY_PLACEHOLDER}>
                <Row className="border-bottom text-light text-16 p-3 align-items-center">
                  <Col lg="4" className="text-right d-none d-lg-block font-weight-bold">
                    {keyOf(item)}{' '}
                    {selectedSource ? (
                      <ScopeBadges source={selectedSource} maps={nameMaps} />
                    ) : (
                      <Badge color="danger">unused</Badge>
                    )}
                  </Col>
                  <Col xs="12" className="d-lg-none font-weight-bold">
                    {keyOf(item)}{' '}
                    {selectedSource ? (
                      <ScopeBadges source={selectedSource} maps={nameMaps} />
                    ) : (
                      <Badge color="danger">unused</Badge>
                    )}
                  </Col>
                  <Col lg="7" md="10" xs="10">
                    <input
                      type={isSensitive ? 'password' : 'text'}
                      className="w-100 p-2 rounded border"
                      value={draftValues[item.key] ?? item.value ?? ''}
                      onChange={e => handleDraftChange(item.key, e.target.value)}
                    />
                  </Col>
                  <Col lg="1" md="2" xs="2" className="d-flex gap-1 justify-content-end">
                    {isDirty(item) && (
                      <Button
                        color="primary"
                        size="sm"
                        disabled={savingKey === item.key}
                        onClick={() => void handleSave(item)}>
                        {savingKey === item.key ? '…' : 'Save'}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        color="danger"
                        size="sm"
                        disabled={removingKey === item.key}
                        onClick={() => void handleRemove(item)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    )}
                    <Button color="secondary" size="sm" onClick={() => toggleExpanded(item.key)}>
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </Button>
                  </Col>
                </Row>

                {isExpanded && item.sources && item.sources.length > 0 && (
                  <Row className="px-3 pb-2">
                    <Col xs="12">
                      <table className="table table-sm table-dark text-light mb-0 mt-1">
                        <thead>
                          <tr>
                            <th>Entity</th>
                            <th>Scope</th>
                            {/* <th>Score</th> */}
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.sources.map((src, idx) => {
                            const isSelected = src.sourceId === item.selectedSourceId;
                            return (
                              <tr key={idx} className={isSelected ? 'table-primary text-dark' : ''}>
                                <td>{src.sourceEntity ?? '—'}</td>
                                <td>
                                  <ScopeBadges source={src} maps={nameMaps} />
                                  <span className="text-muted">({src.score ?? ''})</span>
                                </td>
                                <td>
                                  {isSensitive
                                    ? '••••••'
                                    : (src.value ?? <span className="text-muted">null</span>)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            );
          })}

        {/* Add new config row */}
        <Row className="p-3 align-items-center">
          <Col lg="4" className="d-none d-lg-block text-right font-weight-bold text-light">
            New configuration
          </Col>
          <Col xs="12" className="d-lg-none font-weight-bold text-light mb-1">
            New configuration
          </Col>
          <Col lg="4" md="6" xs="6">
            <input
              className="w-100 p-2 rounded border"
              placeholder="Key"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              disabled={loading || saving}
            />
          </Col>
          <Col lg="3" md="4" xs="4">
            <input
              className="w-100 p-2 rounded border"
              placeholder="Value"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              disabled={loading || saving}
            />
          </Col>
          <Col lg="1" md="2" xs="2">
            <Button
              color="primary"
              size="sm"
              disabled={!newKey.trim() || !newValue.trim() || saving || loading}
              onClick={() => void handleAdd()}>
              {saving ? '…' : <FontAwesomeIcon icon={faPlus} />}
            </Button>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="justify-content-end">
        <Button color="info" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
